import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import API, { formatPrice, getImageUrl } from "@/lib/api";
import { Minus, Plus, ShoppingBag, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function CartPage() {
  const { cart, fetchCart, removeFromCart, updateQuantity } = useCart();
  const { user } = useAuth();
  const { t, locale } = useLanguage();
  const navigate = useNavigate();
  const [notes, setNotes] = useState({}); // item_id -> note string
  const [showNoteInput, setShowNoteInput] = useState({}); // item_id -> bool

  const isReseller = ["reseller", "agen", "mitra", "produsen", "distributor", "admin"].includes(user?.role);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  const items = cart.items || [];

  // Initialize notes from cart items
  useEffect(() => {
    const initialNotes = {};
    const initialShow = {};
    for (const item of items) {
      if (item.item_notes) {
        initialNotes[item.item_id] = item.item_notes;
        initialShow[item.item_id] = true;
      }
    }
    setNotes(prev => ({ ...initialNotes, ...prev }));
    setShowNoteInput(prev => ({ ...initialShow, ...prev }));
  }, [items.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const getEffectivePrice = (product) => {
    if (!product) return 0;
    return product.pricing?.final_price || product.price || 0;
  };

  const getDiscount = (product) => {
    if (!product?.pricing || (product.pricing.savings || 0) <= 0) return null;
    return {
      original: product.pricing.base_price,
      pct: product.pricing.discount_pct,
      type: product.pricing.discount_type,
      value: product.pricing.discount_value,
    };
  };

  const total = items.reduce((acc, item) => {
    return acc + getEffectivePrice(item.product) * item.quantity;
  }, 0);
  const normalTotal = items.reduce((acc, item) => {
    const basePrice = item.product?.pricing?.base_price || item.product?.price || getEffectivePrice(item.product);
    return acc + basePrice * item.quantity;
  }, 0);
  const totalSavings = Math.max(0, normalTotal - total);
  const totalDiscountPct = normalTotal > 0 && totalSavings > 0 ? Math.round((totalSavings / normalTotal) * 100) : 0;

  const totalItems = items.reduce((acc, item) => acc + item.quantity, 0);

  const toggleNote = (itemId) => {
    setShowNoteInput(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const saveItemNotes = async (itemId) => {
    try {
      await API.put(`/cart/notes/${itemId}`, { item_notes: notes[itemId] || "" });
    } catch {
      // silent
    }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="cart-page">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <h1 className="font-serif text-3xl text-stone-900 mb-8" data-testid="cart-title">{t("cart.title")}</h1>

        {items.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-stone-100">
            <ShoppingBag className="w-14 h-14 text-stone-200 mx-auto mb-4" />
            <p className="text-stone-500 mb-4 text-sm">{t("cart.empty")}</p>
            <Link to="/products" className="inline-block text-sm text-primary-foreground bg-primary hover:opacity-90 px-6 py-2.5 rounded-lg transition-colors">
              {t("cart.continue_shopping")}
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {items.map((item) => {
                const discount = getDiscount(item.product);
                const effectivePrice = getEffectivePrice(item.product);

                return (
                  <div key={item.item_id} className="bg-white rounded-xl border border-stone-100 p-4 sm:p-5" data-testid={`cart-item-${item.item_id}`}>
                    <div className="flex gap-4">
                      {/* Thumbnail */}
                      <Link to={`/products/${item.product_id}`} className="w-20 h-20 sm:w-24 sm:h-24 bg-stone-100 rounded-lg flex-shrink-0 overflow-hidden block hover:opacity-90 transition-opacity">
                        <img src={item.product?.images?.[0] ? getImageUrl(item.product.images[0]) : ""} alt={item.product?.name || ""} className="w-full h-full object-cover" />
                      </Link>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        {/* Product Name */}
                        <Link to={`/products/${item.product_id}`} className="font-sans text-sm sm:text-base font-bold text-stone-900 hover:text-primary transition-colors line-clamp-2 block mb-1.5">
                          {item.product?.name} by {item.product?.brand_name || item.product?.brand?.name || item.product?.brand_id?.replace("brand_", "")}
                        </Link>

                        {/* Price Row */}
                        <div className="flex items-center gap-2 flex-wrap mb-2.5">
                          {discount && (
                            <>
                              <span className="text-xs text-stone-400 line-through">{formatPrice(discount.original)}</span>
                              <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">
                                {locale === "id" ? "Diskon" : "Off"} {discount.pct}%
                              </span>
                            </>
                          )}
                          <span className="text-sm sm:text-base font-bold text-stone-900">{formatPrice(effectivePrice)}</span>
                        </div>

                        {/* Variant Chips */}
                        {item.variant && (item.variant.color || item.variant.size) && (
                          <div className="flex flex-wrap gap-1.5 mb-3">
                            {item.variant.color && (
                              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-stone-600 bg-stone-100 px-2.5 py-1 rounded-full">
                                {item.variant.color}
                              </span>
                            )}
                            {item.variant.size && (
                              <span className="inline-flex items-center text-[11px] font-medium text-stone-600 bg-stone-100 px-2.5 py-1 rounded-full">
                                {item.variant.size}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Add/Edit Note */}
                        <button
                          onClick={() => toggleNote(item.item_id)}
                          className="flex items-center gap-1.5 text-xs text-primary hover:opacity-80 transition-colors mb-3"
                        >
                          <Pencil className="w-3 h-3" />
                          {notes[item.item_id]
                            ? (locale === "id" ? "Edit Catatan" : "Edit Note")
                            : (locale === "id" ? "Tambah Catatan" : "Add Note")}
                        </button>

                        {showNoteInput[item.item_id] && (
                          <input
                            type="text"
                            value={notes[item.item_id] || ""}
                            onChange={(e) => setNotes(prev => ({ ...prev, [item.item_id]: e.target.value }))}
                            onBlur={() => saveItemNotes(item.item_id)}
                            placeholder={locale === "id" ? "Contoh: tolong packing rapi ya" : "e.g. please pack carefully"}
                            className="w-full text-xs border border-stone-200 rounded-lg px-3 py-2 mb-3 focus:outline-none focus:border-primary bg-white placeholder:text-stone-300"
                          />
                        )}

                        {/* Saved note display (when input is hidden) */}
                        {!showNoteInput[item.item_id] && notes[item.item_id] && (
                          <p className="text-xs text-stone-500 italic mb-3 bg-stone-50 rounded px-2.5 py-1.5 border border-stone-100">
                            📝 {notes[item.item_id]}
                          </p>
                        )}

                        {/* Bottom Row: Qty + Delete */}
                        <div className="flex items-center justify-between">
                          {/* Qty Controls */}
                          <div className="flex items-center border border-stone-200 rounded-lg overflow-hidden">
                            <button
                              onClick={() => updateQuantity(item.item_id, item.quantity - 1)}
                              disabled={item.quantity <= 1}
                              className="w-8 h-8 flex items-center justify-center text-stone-500 hover:bg-stone-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            >
                              <Minus className="w-3.5 h-3.5" />
                            </button>
                            <span className="w-10 h-8 flex items-center justify-center text-sm font-semibold text-stone-800 border-x border-stone-200 bg-white">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => updateQuantity(item.item_id, item.quantity + 1)}
                              className="w-8 h-8 flex items-center justify-center text-stone-500 hover:bg-stone-50 transition-colors"
                            >
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                          </div>

                          {/* Delete */}
                          <button
                            onClick={() => {
                              if (window.confirm(locale === "id" ? "Hapus item ini?" : "Remove this item?")) {
                                removeFromCart(item.item_id);
                                toast.success(locale === "id" ? "Item dihapus" : "Item removed");
                              }
                            }}
                            className="text-xs font-medium text-red-400 hover:text-red-600 transition-colors"
                            data-testid={`remove-item-${item.item_id}`}
                          >
                            {locale === "id" ? "Hapus" : "Remove"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Sticky Summary Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl border border-stone-100 p-5 sticky top-24">
                <h2 className="font-sans text-sm font-bold text-stone-900 mb-4 uppercase tracking-wider">
                  {locale === "id" ? "Ringkasan Belanja" : "Shopping Summary"}
                </h2>

                <div className="space-y-2.5 mb-5 text-sm">
                  <div className="flex justify-between text-stone-500">
                    <span>{t("cart.subtotal")} ({totalItems} {t("cart.items")})</span>
                    <div className="text-right">
                      {totalSavings > 0 && (
                        <span className="block text-xs text-stone-400 line-through">{formatPrice(normalTotal)}</span>
                      )}
                      <span className="text-stone-700 font-medium">{formatPrice(total)}</span>
                    </div>
                  </div>
                  {totalSavings > 0 && (
                    <div className="flex justify-between text-red-500">
                      <span>{locale === "id" ? "Total Diskon" : "Total Discount"}</span>
                      <span className="font-medium">
                        -{formatPrice(totalSavings)}
                        {totalDiscountPct > 0 && <span className="text-xs ml-1 text-red-400">(-{totalDiscountPct}%)</span>}
                      </span>
                    </div>
                  )}
                </div>

                <div className="border-t border-stone-100 pt-4 mb-5">
                  <div className="flex justify-between items-center gap-3">
                    <span className="font-bold text-stone-900">{locale === "id" ? "Total Harga" : "Total Price"}</span>
                    <div className="text-right">
                      {totalSavings > 0 && (
                        <span className="block text-xs text-stone-400 line-through">{formatPrice(normalTotal)}</span>
                      )}
                      <span className="text-xl font-bold text-stone-900" data-testid="cart-total">{formatPrice(total)}</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => navigate("/checkout")}
                  className="w-full bg-primary text-primary-foreground py-3.5 text-sm font-semibold rounded-lg uppercase tracking-wider hover:opacity-90 transition-colors"
                  data-testid="checkout-btn"
                >
                  {t("cart.checkout")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
